import { ChartData, ChartOptions } from "chart.js";
import i18n from "common/languageHandling/i18n";
import { t } from "i18next";
import moment from "moment";
import { IValues, IInstallationDataResult } from "nodeDetails/types";
import { getFirstRealValue, getMaxFromDataset } from "./dataFunctions";
import { longMonthNames } from "./dateFunctions";
import { generateLabeldependingOnPeriod } from "./handleIntervalAndChartLabels";

export const getLineData = (
  selectedPeriod: string,
  lineData: IInstallationDataResult[]
): ChartData => {
  let data: ChartData;

  let labels: string[] | number[] = [];
  if (lineData[0].Values)
    labels = generateLabeldependingOnPeriod(selectedPeriod, lineData[0].Values);
  if (lineData.length > 1) {
    data = {
      labels: labels,
      datasets: [
        {
          type: "line" as const,
          label: i18n.t("Temperature"),
          data: lineData[0].Values.map((d) => d.Value),
          pointRadius: 0,
          borderWidth: 1,
          borderColor: "#9EBECB",
          yAxisID: "y",
        },
        {
          type: "line" as const,
          label: i18n.t("Relative humidity"),
          data: lineData[1].Values.map((d) => d.Value),
          pointRadius: 0,
          borderWidth: 1,
          borderColor: "#304855",
          backgroundColor: "#304855",
          yAxisID: "rh",
        },
      ],
    };
  } else {
    data = {
      labels: labels,
      datasets: [
        {
          type: "line",
          label: i18n.t("Temperature"),
          data: lineData[0].Values.map((d) => d.Value),

          pointRadius: 0,
          borderWidth: 1,
          borderColor: "#9EBECB",
        },
      ],
    };
  }
  return data;
};

export const getBarData = (
  selectedPeriod: string,
  currentData: IValues[],
  previousData?: IValues[] | null
) => {
  const labels = generateLabeldependingOnPeriod(selectedPeriod, currentData);
  const currentTime = moment().unix();
  const previousDataCopy = previousData?.map((d) => {
    return {
      Time: d.Time,
      Value: d.Value,
    };
  });
  const currentBackgroundColors = currentData.map((d) => {
    if (d) return "#8caab9";
  });

  const remainingTime = labels.length - currentBackgroundColors.length;
  const prognosBackgroundColors = [];
  for (let i = 0; i < currentData.length; i++) {
    if (i >= remainingTime) prognosBackgroundColors.push("#c4c4c4");
    else {
      prognosBackgroundColors.push("");
    }
  }

  let firstNullIndex = 0;
  for (let i = currentData.length - 1; i >= 0; i--) {
    if (currentData[i].Value !== null && currentData[i].Value !== 0) {
      firstNullIndex = i + 1;
      break;
    }
  }

  const currentDataAvg =
    currentData
      .slice(0, firstNullIndex)
      .reduce((acc, obj) => acc + obj.Value, 0) / firstNullIndex;
  const previousDataChopped = previousData?.slice(0, firstNullIndex) ?? [];
  const previousDataAvg =
    previousDataChopped.reduce((acc, obj) => acc + obj.Value, 0) /
    firstNullIndex;
  const percentage = currentDataAvg / previousDataAvg;

  const shouldShowTrendLine =
    currentTime >= currentData[0].Time &&
    currentTime <= currentData[currentData.length - 1].Time;

  const currentDataFirstValue = getFirstRealValue(currentData);
  const previousDataFirstValue = getFirstRealValue(previousData ?? []);
  let currentDataYear = new Date(
    (currentDataFirstValue?.Time ?? 0) * 1000
  ).getFullYear();
  let previousDataYear = new Date(
    (previousDataFirstValue?.Time ?? 0) * 1000
  ).getFullYear();

  const previousYearData = {
    type: "line" as const,
    label: `Trend`,
    data:
      previousData?.map((d, i) => {
        if (firstNullIndex > 0 && i >= firstNullIndex - 1)
          return d.Value * percentage;

        return null;
      }) || [],
    pointRadius: 0,
    backgroundColor: "#304855",
    borderColor: "#304855",
    borderWidth: 1,
  };

  const currentYearData = {
    type: "bar" as const,
    borderWidth: 0,
    borderRadius: 100,
    label: `${currentDataYear === 1970 ? "" : currentDataYear}`,
    data: currentData.map((d) => d.Value) || [],
    backgroundColor: currentBackgroundColors,
    barThickness: 3.5,
  };

  const previousYearDataCopy = {
    type: "bar" as const,
    borderWidth: 0,
    borderRadius: 100,
    label: `${previousDataYear === 1970 ? "" : previousDataYear}`,
    data: previousDataCopy?.map((d) => d.Value) || [],
    backgroundColor: prognosBackgroundColors,
    barThickness: 3.5,
  };

  const data: ChartData = {
    labels: labels,
    datasets: shouldShowTrendLine
      ? [previousYearData, currentYearData, previousYearDataCopy]
      : [currentYearData, previousYearDataCopy],
  };
  return data;
};

export const getLineChartOptions = (
  suggestedMax: number[],
  relativeHumidityData: boolean,
  title: string
) => {
  let options: ChartOptions<"line"> = {
    plugins: {
      tooltip: {
        displayColors: false,
        callbacks: {
          title: function (context) {
            const week = context[0].dataset.data.length === 7;
            const month = title.replace(/\d+/g, "");
            return week
              ? `${context[0].label}`
              : `${context[0].label} ${month} `;
          },
        },
        titleAlign: "center",
        titleColor: "#9DE4D0",
      },
      legend: {
        display: true,
        rtl: true,
        reverse: true,
        labels: {
          boxWidth: 24,
          boxHeight: 1,
        },
      },
    },
    scales: {
      x: {
        ticks: {
          font: {
            size: 10,
            weight: "400",
          },
          color: "#083535",
          maxRotation: 0,
          minRotation: 0,
        },
        beginAtZero: true,
        grid: {
          display: false,
          borderWidth: 0,
        },
      },
      y: {
        ticks: {
          callback: function (value) {
            return value + "°C";
          },
          font: {
            size: 10,
            weight: "400",
          },
          color: "#083535",
        },
        suggestedMax: suggestedMax[0] || 35,
        suggestedMin: 15,
        beginAtZero: false,
        type: "linear",
        position: "left",
        grid: {
          borderWidth: 0,
          display: false,
        },
      },
    },
  };
  if (relativeHumidityData) {
    options.scales = {
      ...options.scales,
      rh: {
        type: "linear",
        suggestedMax: suggestedMax[1],
        suggestedMin: 15,
        position: "right",
        grid: {
          borderWidth: 0,
          display: false,
        },
        ticks: {
          callback: function (value) {
            return value + "%";
          },
          font: {
            size: 10,
            weight: "400",
          },
          color: "#083535",
        },
      },
    };
  }
  return options;
};

export const getBarChartOptions = (
  title: string,
  suggestedMax: number,
  unit: string
) => {
  const options: ChartOptions<"bar"> = {
    interaction: {
      mode: "index",
    },
    plugins: {
      title: {
        display: true,
        text: title,
        font: {
          size: 14,
          weight: "400",
        },
        color: "#000000",
      },
      tooltip: {
        displayColors: false,
        callbacks: {
          title: function (context) {
            const week = context[0].dataset.data.length === 7;
            const month = title.replace(/\d+/g, "");
            return week
              ? `${context[0].label}`
              : `${context[0].label} ${month} `;
          },
          label: function (context) {
            let label = context.dataset.label || "";
            if (label) {
              label += `: ${
                context.formattedValue === "0" ? "-" : context.formattedValue
              }`;
            } else {
              ("");
            }
            return label;
          },
        },
        titleAlign: "center",
        titleColor: "#9DE4D0",
      },
      legend: {
        display: true,
        labels: {
          boxWidth: 15,
          boxHeight: 10,
        },
      },
    },
    scales: {
      x: {
        ticks: {
          font: {
            size: 10,
            weight: "400",
          },
          color: "#083535",
          maxRotation: 0,
          minRotation: 0,
        },
        grid: {
          display: false,
          borderWidth: 0,
        },
      },
      y: {
        ticks: {
          callback: function (value, index, ticks) {
            return `${value} ${getTickFromUnit(unit)}`;
          },
          font: {
            size: 10,
            weight: "400",
          },
          color: "#083535",
        },
        suggestedMax: suggestedMax ?? 35,
        beginAtZero: true,
        grid: {
          borderWidth: 0,
          display: false,
        },
      },
    },
  };
  return options;
};

export const getTickFromUnit = (unit: string) => {
  switch (unit) {
    case t("litres"):
      return t("l");
    default:
      return unit;
  }
};
export const setSuggestedMaxForConsumption = (
  data: IInstallationDataResult[]
) => {
  let maxVal = 0;
  data.forEach((d) => {
    const num = Math.max(...d.Values.map((v) => v.Value));
    if (num > maxVal) maxVal = num;
  });
  return Math.round(maxVal / 2) * 2 * 1, 2;
};

export const setSuggestedMaxForIndoorClimate = (
  data: IInstallationDataResult[]
) => {
  if (data.length > 1) {
    const maxNum1 = getMaxFromDataset(data[0]);
    const maxNum2 = getMaxFromDataset(data[1]);

    return [Math.round(maxNum1 / 5) * 5 + 5, Math.round(maxNum2 / 5) * 5 + 5];
  } else {
    const maxNum1 = getMaxFromDataset(data[0]);
    return [Math.round(maxNum1 / 5) * 5 + 5];
  }
};

export const highlightMinAndMaxColor = (numbArray: number[]) => {
  return numbArray.map((val, i) => {
    if (val === Math.max(...numbArray)) {
      return "#ff3806";
    }
    if (val === Math.min(...numbArray.filter((e) => e !== null))) {
      return "rgb(61, 168, 255)";
    }
  });
};

export const highlightMinAndMaxSize = (numbArray: number[]) => {
  return numbArray.map((val, i) => {
    if (val === Math.max(...numbArray)) {
      return 5;
    }
    if (val === Math.min(...numbArray.filter((e) => e !== null))) {
      return 5;
    }
  });
};

export const getTitle = (data: IInstallationDataResult[]) => {
  const value = getFirstRealValue(data.find((r) => r.Values)?.Values ?? []);
  if (!value) return "";
  switch (data[0].Values.length) {
    case 7:
      return `${moment.unix(value.Time).year()}  ${t("Week")} ${moment
        .unix(value.Time)
        .week()}`;
    case 12:
      return `${moment.unix(value.Time).year()}`;
    case 28:
    case 29:
    case 30:
    case 31:
      return `${moment.unix(value.Time).year()} ${
        longMonthNames[moment.unix(value.Time).month()]
      }`;
    default:
      return "";
  }
};
